import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { map, Observable, startWith, Subscription } from 'rxjs';
import { Azienda, AziendaService } from 'src/app/services/azienda/azienda.service';
import {
  BucketService,
  FileExtension,
  fileExtension,
  FileNode,
  FolderNode,
} from 'src/app/services/bucket/bucket.service';
import { Utente, UtenteService } from 'src/app/services/utente/utente.service';
import { BucketStoreService } from '../../bucket-store.service';

export type SortOrder = 'crescente' | 'decrescente';

export interface SortFilesBy {
  type: 'data' | 'size' | undefined;
  order: SortOrder;
}
@Component({
  selector: 'app-search-and-filtri-documenti',
  standalone: false,
  templateUrl: './search-and-filtri-documenti.component.html',
  styleUrl: './search-and-filtri-documenti.component.scss',
})
export class SearchAndFiltriDocumentiComponent implements AfterViewInit, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  @Input() visualizza: 'SEARCHBAR' | 'FILTRI' = 'SEARCHBAR';
  @Output() public isSearchBarOpen = new EventEmitter<boolean>();
  @Output() public isTipoFiltered = new EventEmitter<boolean>();

  private _store = inject(BucketStoreService);

  get folderFiles(): FileNode[] {
    return this._store.selectedFolder()?.files ?? [];
  }

  get listaFolders() {
    return this._store.tree()[0].folders[0].folders;
  }

  effect = effect(() => {
    const folders = this._store.tree();
    if (folders) {
      this.rootFolders = this.listaFolders;

      this._initFilteredObservavle();
    }
  });

  // Definisco una type guard per capire il tipo dell'oggetto
  isFolder(node: FolderNode | FileNode): boolean {
    return 'folders' in node;
  }

  nomeUtenteById = (idUtente: string): string => {
    return this.utenteFilter.find((u) => u.id === idUtente)?.name ?? 'Utente sconosciuto';
  };

  // Ritorno il logo del file
  public getLogoFile(file: FileNode): string {
    return this._bucketService.getLogoFile(file.extension);
  }

  hasSottoCartelle = (folder: FolderNode) => !!folder.folders.length;

  hasFile = (folder: FolderNode) => !!folder.files.length;

  /**
   * Form control per gestire l'input dell'autocomplete.
   */
  searchControl = new FormControl<string>('');

  tipoFilterControl = new FormControl<string[]>([]);
  utenteFilterControl = new FormControl<string[]>([]);

  tipoFileFilter: FileExtension[] = fileExtension.map(
    (elem) => elem.toUpperCase() as FileExtension,
  );

  utenteFilter: { id: string; name: string }[] = [];

  /**
   * Observable che contiene i risultati filtrati per l'autocomplete.
   */
  filteredNodes$: Observable<(FolderNode | FileNode)[]> = new Observable();

  /**
   * Cartella radice da cui partire per la ricerca.
   */
  rootFolders: FolderNode[] = this.listaFolders;

  private azienda: Azienda | undefined = undefined;
  private _subAziendaCorrente: Subscription | undefined;
  constructor(
    private readonly _bucketService: BucketService,
    private readonly _aziendaService: AziendaService,
    public utenteService: UtenteService,
  ) {}

  /**
   * Inizializza l'autocomplete e si iscrive ai cambiamenti dell'input.
   */
  ngOnInit(): void {
    this._initFilteredObservavle();
  }

  ngAfterViewInit() {
    this._onChangeAzienda();
  }

  ngOnDestroy(): void {
    this._subAziendaCorrente?.unsubscribe();
  }

  private _initFilteredObservavle() {
    this.filteredNodes$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterNodes(value || '')), // se null, passo stringa vuota
    );
  }

  /**
   * Filtro ricorsivamente folders e files in base a una chiave di ricerca.
   */
  private filterNodes(searchTerm: string): (FolderNode | FileNode)[] {
    if (typeof searchTerm !== 'string') {
      searchTerm = (searchTerm as any).name;
    }

    if (searchTerm) {
      return this.searchRecursive(this.rootFolders, searchTerm.toLowerCase());
    }
    /*     return this.rootFolders; */
    return [];
  }

  /**
   * Eseguo la ricerca ricorsiva su cartelle e file.
   * @param folders L'array di FolderNode su cui effettuare la ricerca
   * @param searchTerm La chiave di ricerca in minuscolo
   * @returns Array di FolderNode e FileNode che corrispondono alla ricerca
   */
  private searchRecursive(folders: FolderNode[], searchTerm: string): (FolderNode | FileNode)[] {
    const results: (FolderNode | FileNode)[] = [];

    // Cerco corrispondenza nel nome delle cartelle
    for (const folder of folders) {
      if (this.matchesSearch(folder.name, searchTerm)) {
        results.push(folder);
      }

      // Cerco corrispondeza nel nome dei file della cartella iterata
      results.push(...this.filterFiles(folder.files, searchTerm));

      // Se ho altre sotto cartelle, faccio ricorsione del metodo.
      if (folder.folders.length > 0) {
        results.push(...this.searchRecursive(folder.folders, searchTerm));
      }
    }

    // Ritorno la lista filtrata
    return results;
  }

  /**
   * Filtra i file in base al nome.
   * @param files Array di FileNode da filtrare
   * @param searchTerm La chiave di ricerca in minuscolo
   * @returns Array di FileNode che corrispondono alla ricerca
   */
  private filterFiles(files: FileNode[], searchTerm: string): FileNode[] {
    return files.filter((file) => this.matchesSearch(file.name, searchTerm));
  }

  /**
   * Controlla se un nome corrisponde alla chiave di ricerca.
   * @param name Nome del file o della cartella
   * @param searchTerm La chiave di ricerca in minuscolo
   * @returns true se il nome contiene la chiave di ricerca, false altrimenti
   */
  private matchesSearch(name: string, searchTerm: string): boolean {
    return name.toLowerCase().includes(searchTerm);
  }

  /**
   * Funzione di utilità per mostrare il nome di un nodo nell'autocomplete.
   * @param node FolderNode o FileNode
   * @returns Nome del nodo (folder / cartella)
   */
  displayFn(node: FolderNode | FileNode): string {
    return node?.name || '';
  }

  /**
   * Ritorno la path leggeibile, dalla 3 posizione in poi
   * cosi mi salto le prime 2 cartelle che sono utente e azienda
   */
  getVisiblePath(path: string): string {
    if (path) {
      const parts = path.split('/');
      return 'home/' + parts.slice(3).join('/');
    }
    return '';
  }

  optionClicked(node: FolderNode | FileNode, event: MatOptionSelectionChange) {
    if (!event.isUserInput) return;

    this.onBlurInput();

    // Se è una folder allora navigo direttamente alla sua posizione
    if (this.isFolder(node)) {
      this._store.setSelectedFolder(node as FolderNode);
      return;
    }

    // se è un file allora devo trovare la folder dove è posizionato quel file
    const parentFolder = this._findFolderOfFile(node.parentPath);

    if (parentFolder) {
      this._store.setSelectedFolder(parentFolder);
    } else {
      console.error('Nessuna cartella contiene:', node);
    }
  }

  /**
   * Cache interna utilizzata per mappare rapidamente un `path` alla
   * cartella corrispondente senza rieseguire la ricerca ricorsiva.
   *
   * Key: `path` -> della cartella
   * Value: FolderNode -> cartella trovata
   */
  private folderCache = new Map<string, FolderNode>();

  /**
   * Cerca la cartella che contiene un file partendo dal suo `parentPath`.
   *
   * La funzione utilizza una cache interna (`folderCache`) per evitare
   * ricerche ripetute nello stesso albero delle cartelle, migliorando le performance.
   *
   * Il metodo esegue:
   *  1. Controllo in cache.
   *  2. Ricerca ricorsiva all'interno di `listaFolders`.
   *  3. Salvataggio del risultato in cache se trovato.
   *
   * @param {string} parentPathFile - Il path della cartella che contiene il file.
   * @returns {FolderNode | undefined} Ritorna la cartella corrispondente al path
   *                                   oppure `undefined` se non viene trovata.
   */
  private _findFolderOfFile(parentPathFile: string): FolderNode | undefined {
    //  1. se è già in cache, ritorno subito
    if (this.folderCache.has(parentPathFile)) {
      return this.folderCache.get(parentPathFile);
    }

    //  2. cerco nel tree completo
    for (const root of this.listaFolders) {
      const found = this._searchFolderRecursive(root, parentPathFile);
      if (found) {
        this.folderCache.set(parentPathFile, found); //  salvo in cache
        return found;
      }
    }

    console.warn('Folder non trovato per path:', parentPathFile);
    return undefined;
  }

  /**
   * Ricerca ricorsivamente una cartella corrispondente a `targetPath`
   * all'interno di un albero di cartelle.
   *
   * Questo metodo scende in profondità (DFS) attraverso tutte le
   * sotto-cartelle finché non trova un match sul campo `path`.
   *
   * @param {FolderNode} folder - La cartella da cui iniziare la ricerca.
   * @param {string} targetPath - Il path della cartella da trovare.
   * @returns {FolderNode | undefined} Ritorna la cartella se trovata,
   *                                   altrimenti `undefined`.
   */
  private _searchFolderRecursive(folder: FolderNode, targetPath: string): FolderNode | undefined {
    // Match diretto
    if (folder.path === targetPath) return folder;

    // Cerco nelle cartelle figlie
    for (const sub of folder.folders) {
      const found = this._searchFolderRecursive(sub, targetPath);
      if (found) return found;
    }

    return undefined;
  }

  isSearchOpen = false;

  toggleSearch() {
    const isOpen = (this.isSearchOpen = !this.isSearchOpen);
    this.isSearchBarOpen.emit(isOpen);
    if (isOpen) {
      setTimeout(() => this.searchInput.nativeElement.focus(), 0);
    }
    return isOpen;
  }

  onFocusInput() {
    this.isSearchOpen = true;
    this.isSearchBarOpen.emit(true);
  }

  onBlurInput() {
    this.isSearchOpen = false;
    this.isSearchBarOpen.emit(false);
  }

  /**
   * Filtro per tipo file
   */
  onTipoChange() {
    const tipoVal = this.tipoFilterControl.getRawValue() ?? [];
    this._store.tipoFilter.set(tipoVal);
  }
  /**
   * Filtro per utente che ha caricato
   */
  onUtenteChange() {
    const utenti = this.utenteFilterControl.getRawValue() ?? [];
    this._store.utenteFilters.set(utenti);
  }

  private _onChangeAzienda() {
    this._subAziendaCorrente = this._aziendaService.aziendaCorrente.subscribe((azienda) => {
      let cambiato = false;

      if (this.azienda?.id !== azienda?.id) {
        cambiato = true;

        this.azienda = azienda;

        if (cambiato && this.azienda?.id) {
          this._getUtentiPerAzienda(this.azienda.id);
        }
      }
    });
  }

  private _getUtentiPerAzienda(idAzienda: string) {
    this.utenteService.getUtentiByIdAzienda(idAzienda, 0, 1000, '', [], []).subscribe((esito) => {
      if (esito.content) {
        this.utenteFilter = esito.content.map((utente: Utente) => {
          return { id: utente.id, name: utente.firstName + ' ' + utente.lastName };
        });
      }
    });
  }

  public sortSelected: 'data' | 'size' | undefined = undefined;
  public sortOrder: SortOrder = 'decrescente';

  sortFileBy(sortOrder?: SortOrder) {
    const currentSort = this._store.sortFilesBy();

    // Se viene passato un sortOrder lo uso.
    // Altrimenti inverto quello attuale.
    this.sortOrder =
      sortOrder ?? (currentSort?.order === 'crescente' ? 'decrescente' : 'crescente');

    this._store.sortFilesBy.set({
      type: this.sortSelected,
      order: this.sortOrder,
    });
  }

  onSelectSort(sort: 'data' | 'size') {
    let newOrder: SortOrder = 'decrescente';

    // Se Cambio elemento di sort mantengo l'ordine corrente
    if (this.sortSelected && this.sortSelected !== sort) {
      newOrder = this.sortOrder;
    }

    this.sortSelected = sort;
    this.sortFileBy(newOrder);
  }

  clearSort() {
    this.sortSelected = undefined;
    this.sortOrder = 'decrescente';
  }
}
